import { formatCurrency } from '@saaga/shared/utils/billing.util'
import { ChevronDownIcon, Loader2Icon, MinusIcon, PlusIcon, UsersIcon } from 'lucide-react'
import { memo, type ReactNode, useCallback } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

import { BillingAddSeatsConfirm } from './billing-add-seats-confirm.molecule'
import { ADD_SEATS_OPTIONS, type SeatManagerProps } from './billing-seat.interface'
import { useSeatConfirm, useSeatManagerState } from './use-seat-confirm.hook'

const formatSeatPrice = (price: number, interval: 'month' | 'year'): string =>
  `${formatCurrency(price)}/seat/${interval === 'month' ? 'mo' : 'yr'}`

const BillingSeatManagerBase: React.FC<SeatManagerProps> = ({
  seatInfo,
  isLoading = false,
  isAddingSeats = false,
  isRemovingSeats = false,
  onAddSeats,
  onRemoveSeats,
  hideSeatPrice = false,
  className,
}): ReactNode => {
  const { usedSeats, totalSeats, seatPrice, billingInterval } = seatInfo
  const confirm = useSeatConfirm()
  const state = useSeatManagerState({ seatInfo, isLoading, isAddingSeats, isRemovingSeats })

  const handleConfirm = useCallback(
    (): void => confirm.handleConfirmPurchase(onAddSeats),
    [confirm, onAddSeats]
  )

  return (
    <div className={cn('billing-seat-manager space-y-3', className)}>
      <Separator />
      <SeatHeader />
      <SeatUsageRow
        usedSeats={usedSeats}
        totalSeats={totalSeats}
        seatPrice={seatPrice}
        billingInterval={billingInterval}
        hideSeatPrice={hideSeatPrice}
      />
      <SeatActions
        isAddingSeats={isAddingSeats}
        isRemovingSeats={isRemovingSeats}
        isDisabled={state.isDisabled}
        canRemoveSeats={state.canRemoveSeats}
        removableOptions={state.removableOptions}
        onAddSeatsRequest={confirm.handleAddSeatsRequest}
        onRemoveSeats={onRemoveSeats}
      />
      <BillingAddSeatsConfirm
        isOpen={confirm.isConfirmOpen}
        onOpenChange={confirm.handleConfirmClose}
        quantity={confirm.confirmQuantity ?? 1}
        pricePerSeat={seatPrice}
        billingInterval={billingInterval}
        isLoading={isAddingSeats}
        onConfirm={handleConfirm}
        isEarlyAccess={hideSeatPrice}
      />
    </div>
  )
}

const SeatHeader: React.FC = (): ReactNode => (
  <div className="flex items-center gap-2">
    <UsersIcon className="size-4 text-muted-foreground" />
    <span className="text-sm font-medium">Seats</span>
  </div>
)

interface SeatInfoRowProps {
  usedSeats: number
  totalSeats: number
  price: number
  interval: 'month' | 'year'
}

const SeatInfoRow: React.FC<SeatInfoRowProps> = ({
  usedSeats,
  totalSeats,
  price,
  interval,
}): ReactNode => (
  <div className="flex items-center justify-between">
    <span className="billing-seat-usage text-sm text-muted-foreground">
      {usedSeats} / {totalSeats} {totalSeats === 1 ? 'seat' : 'seats'} used
    </span>
    <span className="billing-seat-price text-sm text-muted-foreground">
      {formatSeatPrice(price, interval)}
    </span>
  </div>
)

interface EarlyAccessSeatInfoRowProps {
  usedSeats: number
  totalSeats: number
}

const EarlyAccessSeatInfoRow: React.FC<EarlyAccessSeatInfoRowProps> = ({
  usedSeats,
  totalSeats,
}): ReactNode => (
  <div className="flex items-center justify-between">
    <span className="billing-seat-usage text-sm text-muted-foreground">
      {usedSeats} / {totalSeats} {totalSeats === 1 ? 'seat' : 'seats'} used
    </span>
  </div>
)

interface SeatUsageRowProps {
  usedSeats: number
  totalSeats: number
  seatPrice: number
  billingInterval: 'month' | 'year'
  hideSeatPrice: boolean
}

const SeatUsageRow: React.FC<SeatUsageRowProps> = ({
  usedSeats,
  totalSeats,
  seatPrice,
  billingInterval,
  hideSeatPrice,
}): ReactNode =>
  hideSeatPrice ? (
    <EarlyAccessSeatInfoRow usedSeats={usedSeats} totalSeats={totalSeats} />
  ) : (
    <SeatInfoRow
      usedSeats={usedSeats}
      totalSeats={totalSeats}
      price={seatPrice}
      interval={billingInterval}
    />
  )

interface SeatActionsProps {
  isAddingSeats: boolean
  isRemovingSeats: boolean
  isDisabled: boolean
  canRemoveSeats: boolean
  removableOptions: number[]
  onAddSeatsRequest: (quantity: number) => void
  onRemoveSeats: (quantity: number) => void
}

const SeatActions: React.FC<SeatActionsProps> = (props): ReactNode => (
  <div className="flex gap-2">
    <AddSeatsDropdown
      isLoading={props.isAddingSeats}
      isDisabled={props.isDisabled}
      onAddSeatsRequest={props.onAddSeatsRequest}
    />
    <RemoveSeatsDropdown
      isLoading={props.isRemovingSeats}
      isDisabled={props.isDisabled || !props.canRemoveSeats}
      optionList={props.removableOptions}
      onRemoveSeats={props.onRemoveSeats}
    />
  </div>
)

interface AddSeatsDropdownProps {
  isLoading: boolean
  isDisabled: boolean
  onAddSeatsRequest: (quantity: number) => void
}

const AddSeatsDropdown: React.FC<AddSeatsDropdownProps> = ({
  isLoading,
  isDisabled,
  onAddSeatsRequest,
}): ReactNode => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        variant="outline"
        size="sm"
        disabled={isDisabled}
        className="billing-add-seats-button"
      >
        {isLoading ? (
          <Loader2Icon className="mr-2 size-4 animate-spin" />
        ) : (
          <PlusIcon className="mr-2 size-4" />
        )}
        Add seats
        <ChevronDownIcon className="ml-2 size-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start">
      {ADD_SEATS_OPTIONS.map((qty) => (
        <DropdownMenuItem
          key={qty}
          onClick={() => onAddSeatsRequest(qty)}
          className="billing-add-seats-option"
        >
          Add {qty} {qty === 1 ? 'seat' : 'seats'}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
)

interface RemoveSeatsDropdownProps {
  isLoading: boolean
  isDisabled: boolean
  optionList: number[]
  onRemoveSeats: (quantity: number) => void
}

const RemoveSeatsDropdown: React.FC<RemoveSeatsDropdownProps> = ({
  isLoading,
  isDisabled,
  optionList,
  onRemoveSeats,
}): ReactNode => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        variant="outline"
        size="sm"
        disabled={isDisabled}
        className="billing-remove-seats-button"
      >
        {isLoading ? (
          <Loader2Icon className="mr-2 size-4 animate-spin" />
        ) : (
          <MinusIcon className="mr-2 size-4" />
        )}
        Remove seats
        <ChevronDownIcon className="ml-2 size-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start">
      {optionList.map((qty) => (
        <DropdownMenuItem
          key={qty}
          onClick={() => onRemoveSeats(qty)}
          className="billing-remove-seats-option"
        >
          Remove {qty} {qty === 1 ? 'seat' : 'seats'}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
)

export const BillingSeatManager = memo(BillingSeatManagerBase)

BillingSeatManager.displayName = 'BillingSeatManager'
